import { useEffect, useRef } from 'react';

/**
 * ★ WAVE U-f — the ↑/↓ library/rail walk: `view/main.js:51-62` (the listener)
 * and `view/main.js:100-174` (`keydown`, the walk itself).
 *
 * Legacy installs ONE `body` handler — `$('body').off('keydown.filterNavigation')
 * .on('keydown.filterNavigation', …)` — that ignores the key unless the event
 * target is outside a text field, then hands ↑/↓ to `View.keydown`. It is page
 * SHELL behaviour (it reaches into whichever tab the hash names), so it is
 * mounted from `UserSettingsPage` and not from either tab.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ★★ WHY THIS IS A DOM HANDLER AND NOT REACT STATE.
 *
 * The walk has two phases, 300 ms apart, and they are observably different:
 *   1. IMMEDIATELY: `.active` is moved from the current row to its neighbour by
 *      hand (`classList.remove`/`add`) and the neighbour is scrolled into view.
 *      Nothing else happens — no selection, no cross-highlight, no pane change.
 *   2. AFTER a 300 ms debounce: a `mouseover` and a `click` are SYNTHESISED on
 *      the neighbour (on its `a.item-for-drag` / `a.submittal-item-for-drag` if
 *      it has one, else on the row itself), and it is those two events that
 *      drive the real handlers — `rowClick`'s rail→library cross-highlight,
 *      `clickToItem`, and `loadPdf`'s lazy PDF fill.
 * Holding an arrow key therefore lights up rows one by one while only the row
 * you STOP on is actually selected. Reproducing that through React state would
 * mean inventing a second, parallel "pre-selected" concept the legacy view does
 * not have; moving the class by hand and letting the synthesised click drive the
 * existing `onRowClick` / `onItemClick` handlers is both smaller and exact. The
 * click's own re-render then rewrites `className` on both rows from props, so
 * the hand-applied class is reconciled away rather than left to rot.
 *
 * Verified on the legacy page (dev3, `urls_legacy` server), which is what every
 * behaviour below is transcribed from rather than from the description:
 *  · boot state = one library `<li>` active, rail empty → ↓ walks the LIBRARY;
 *  · click a rail row first → ↓/↑ walk the RAIL, and 300 ms later the library
 *    cross-highlight follows (`row-126` → ↓ → `row-253` → lib `…field_399`);
 *  · at the bottom of the rail the walk STOPS on the last real row: `.empty-row`
 *    is skipped by the `while` loops and never goes active (measured
 *    `activeIndex 17` of `lastIndex 18`, `emptyRowActive false`);
 *  · nothing active at all (switch to the CMS chip) → ↑ does NOTHING, ↓ lights
 *    the FIRST rail row via `formatFieldChild`;
 *  · a search filter does NOT stop the library walk — `.hide` rows are walked
 *    THROUGH, and the active row can be an invisible one (measured);
 *  · ★ the whole walk is ZERO-NETWORK — see the `detail === 0` note below.
 * ═════════════════════════════════════════════════════════════════════════════
 */

interface NavSelectors {
  formatField: string;
  formatFieldChild: string;
  definedField: string;
  formatScrollable: string;
  definedScrollable: string;
}

/**
 * `self.proposal_submittal_selectors` (`view/main.js:24-39`), transcribed
 * byte-for-byte — including the fact that it is keyed by the raw
 * `window.location.hash` string, which is what makes Q2 reachable.
 */
const PROPOSAL_SUBMITTAL_SELECTORS: Record<string, NavSelectors> = {
  '#proposal': {
    formatField: '#proposal-format-list-wrapper-row .select-row.active',
    formatFieldChild: '#proposal-format-list-wrapper-row .select-row:nth-child(1)',
    definedField: '#proposal-row-container .proposal-row.show-row .each-element-row.active',
    formatScrollable: '#proposal-format-list-wrapper',
    definedScrollable: '#proposal-row-container .proposal-row.show-row .udf-scroll',
  },
  '#submittal': {
    formatField: '#submittal-format-list-wrapper-row .select-row.active',
    formatFieldChild: '#submittal-format-list-wrapper-row .select-row:nth-child(1)',
    definedField: '#submittal-row-container .submittal-row.show-row .each-element-row.active',
    formatScrollable: '#submittal-format-list-wrapper',
    definedScrollable: '#submittal-row-container .submittal-row.show-row .udf-scroll',
  },
};

/**
 * `main.js:148-149,168-169`. A LIBRARY row wraps its anchor, so the events land
 * on the `<a>` (which is what `loadPdf`'s `mouseover .submittal-row
 * .user-fields-block.nav-tabs a` binding needs); a RAIL row contains no `<a>` at
 * all — the proposal rail row carries `item-for-drag` on the `<div>`, and this
 * selector is tag-qualified — so it falls back to the row itself.
 */
const ROW_ANCHOR = 'a.item-for-drag, a.submittal-item-for-drag';

/** `main.js:111-117`, including the asymmetric `- 10` on both edges. */
const isInView = (el: HTMLElement, container: HTMLElement) => {
  const elTop = el.offsetTop;
  const elBottom = elTop + el.offsetHeight;
  const containerTop = container.scrollTop;
  const containerBottom = containerTop + container.clientHeight;
  return elTop - 10 >= containerTop && elBottom - 10 <= containerBottom;
};

export function useFilterNavigation(): void {
  /** `this.keydownTimer` (`main.js:146-151,166-171`) — ONE timer for both
   * directions, cleared on every press, so a held/rapid arrow only ever
   * "commits" the row it comes to rest on. */
  const keydownTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    /** The 300 ms tail: `mouseover` then `click`, on the row's anchor if it has
     * one. `bubbles: true` matters — React's delegated listeners sit on the root
     * container, exactly as Backbone's sat on `#ksdm-content`. */
    const commit = (row: Element) => {
      window.clearTimeout(keydownTimer.current);
      keydownTimer.current = window.setTimeout(() => {
        // The anchor is resolved INSIDE the timer, exactly as legacy does
        // (`main.js:148-149`): the row can be re-rendered during the 300 ms, and
        // resolving early would leave us holding a detached `<a>`.
        const el = (row.querySelector<HTMLElement>(ROW_ANCHOR) ?? row) as HTMLElement;
        el.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));
        el.click();
        window.clearTimeout(keydownTimer.current);
      }, 300);
    };

    const walk = (key: 38 | 40, e: KeyboardEvent) => {
      // ★★ BOTH OF THESE RUN BEFORE Q2 CAN THROW, AND THE FIRST IS MEASURABLE.
      // `main.js:101-102` calls them as the first two statements of `keydown`,
      // ahead of the selector lookup on :103, with nothing between them that can
      // throw. Measured on legacy with NO hash: a listener added on `document`
      // (one level above `body`) never saw the event, so `stopPropagation` had
      // already run — and therefore so had `preventDefault`, which means the
      // arrow key is swallowed rather than scrolling anything, and only THEN
      // does the handler die. Order preserved.
      e.stopPropagation();
      e.preventDefault();

      /**
       * ★★ Q2 — ARROW KEYS "CRASH" WHEN THE HASH IS NOT `#submittal`/`#proposal`.
       *
       * `main.js:103` indexes `this.proposal_submittal_selectors[window.location
       * .hash]` and dereferences the result unguarded, so any hash outside those
       * two keys yields `undefined` and `undefined.formatField` throws.
       *
       * MEASURED on the legacy page rather than assumed (wave 4's lesson):
       *   · `/user_settings/` with NO hash → `TypeError: Cannot read properties
       *     of undefined (reading 'formatField')`, uncaught, page keeps working,
       *     nothing gains or loses `.active`, zero network. This is the DEFAULT
       *     LANDING STATE — the hash stays empty until a tab is clicked (see
       *     `useUserSettingsTab.ts`), so it is reachable for real, not
       *     theoretical.
       *   · `#color` throws identically — the colour tab has no arrow navigation
       *     for the same reason, which is easy to mistake for "not implemented".
       *   · Reaching the SAME state by pressing Back (hash moves, view does not)
       *     is covered too, because the lookup reads the live hash, not the tab
       *     state.
       *
       * CONTAINMENT — the same treatment Q7 got in the Partners port and the two
       * unguarded lookups got in U-d/U-e: reproduce the OUTCOME (the two calls
       * above already happened; the walk now does nothing) without letting an
       * exception escape into React's tree, where an error thrown from a
       * `document`-level listener is not caught by React but WOULD be reported
       * by any error-overlay/monitoring the app grows later. The only thing not
       * reproduced is the console noise, which discovery §13 explicitly
       * recommended dropping and recording as a deviation.
       */
      const selectors = PROPOSAL_SUBMITTAL_SELECTORS[window.location.hash];
      if (!selectors) return;

      const formatField = document.querySelector<HTMLElement>(selectors.formatField);
      const definedField = document.querySelector<HTMLElement>(selectors.definedField);

      let scrollable: HTMLElement | null = null;
      let next: Element | null = null;
      let prev: Element | null = null;

      if (formatField) {
        // ★ THE SENTINEL SKIP. `FormatRail` / `ProposalFormatRail` end the list
        // with `<div class="select-row empty-row">` (the "append to the end"
        // drop target) for the `render` variant, and `renderSelects` drops it.
        // It is a `.select-row` like any other, so without these two loops ↓
        // would light an empty 0-height strip below the last row.
        next = formatField.nextElementSibling;
        while (next && next.classList.contains('empty-row')) next = next.nextElementSibling;
        prev = formatField.previousElementSibling;
        while (prev && prev.classList.contains('empty-row')) prev = prev.previousElementSibling;
        scrollable = document.querySelector<HTMLElement>(selectors.formatScrollable);
      } else if (definedField) {
        // ★ NO skip on this branch — not even of `.hide`, the class the search
        // box puts on non-matching rows. Legacy walks straight through hidden
        // rows and will happily leave the selection on an invisible one
        // (measured with `damper` typed in: 39 of 283 visible, the walk stepped
        // 1→2→3 through hidden rows). Reproduced by omission, deliberately.
        next = definedField.nextElementSibling;
        prev = definedField.previousElementSibling;
        scrollable = document.querySelector<HTMLElement>(selectors.definedScrollable);
      }

      if (key === 38) {
        // ArrowUp. Note there is NO `formatFieldChild` fallback here — with
        // nothing active, ↑ is a no-op (measured), only ↓ can start the walk.
        if (prev) {
          if (formatField) {
            formatField.classList.remove('active');
            prev.classList.add('active');
          } else if (definedField) {
            definedField.classList.remove('active');
            prev.classList.add('active');
          }
          if (scrollable && !isInView(prev as HTMLElement, scrollable)) {
            prev.scrollIntoView({ behavior: 'smooth', block: 'end' });
          }
          commit(prev);
        }
      } else {
        // ArrowDown. The cold-start branch: nothing active anywhere → jump to
        // the FIRST rail row. `scrollable` is null here (neither field existed),
        // so this one case does not scroll — legacy's omission, kept.
        if (!next && !formatField && !definedField) {
          next = document.querySelector(selectors.formatFieldChild);
        }
        if (next) {
          if (formatField) {
            formatField.classList.remove('active');
            next.classList.add('active');
          } else if (definedField) {
            definedField.classList.remove('active');
            next.classList.add('active');
          } else next.classList.add('active');
          if (scrollable && !isInView(next as HTMLElement, scrollable)) {
            next.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          commit(next);
        }
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      // `main.js:52-57` — the text-entry guard, in its original order. Note it
      // returns BEFORE `preventDefault`, so ↑/↓ keep moving the caret inside a
      // field. `SELECT` is deliberately absent: legacy does not list it.
      const target = e.target as HTMLElement | null;
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA') return;
      if (target?.isContentEditable) return;
      // `e.which === 38 || e.which === 40`. `e.key` is the non-deprecated
      // spelling of the same two keys (numpad ↑/↓ with NumLock off report both
      // `which` 38/40 and `key` Arrow*), and it is what `usePopupKeys` uses.
      if (e.key === 'ArrowUp') walk(38, e);
      else if (e.key === 'ArrowDown') walk(40, e);
    };

    // `$('body').on(...)`, and the `.off('keydown.filterNavigation')` that
    // precedes it in legacy is what the effect cleanup gives for free — so
    // there is exactly ONE of these, unlike the three Esc listeners of Q17.
    document.body.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(keydownTimer.current);
    };
  }, []);
}
